type WithId = { id?: string; _id?: string };

const getId = <T extends WithId>(item: T): string => item.id ?? item._id ?? '';

/**
 * Reorders `items` so that any element whose id matches `targetIds` is moved
 * to the front, preserving the order specified by `targetIds`. Items that do
 * not match are kept in their original relative order. Returns the same array
 * reference when there is nothing to reorder, to avoid breaking referential
 * equality in memoized parents.
 *
 * Used by menu section pages to support marketing deep links such as
 * `/menu/section/<id>?targetItemIds=<id1>,<id2>` that pin promoted items to
 * the top of the section.
 */
export const pinTargetItems = <T extends WithId>(items: T[], targetIds: string[]): T[] => {
  // Same reference out when there is nothing to do
  if (!targetIds.length || !items.length) {
    return items;
  }

  // Map id -> URL position. First occurrence wins so duplicates in the URL
  // don't shift the resulting order.
  const order = new Map<string, number>();
  for (const [i, id] of targetIds.entries()) {
    if (id && !order.has(id)) {
      order.set(id, i);
    }
  }

  const pinned: T[] = [];
  const rest: T[] = [];
  for (const item of items) {
    if (order.has(getId(item))) {
      pinned.push(item);
    } else {
      rest.push(item);
    }
  }

  // No matches → fall back to the original list.
  if (pinned.length === 0) {
    return items;
  }

  // Pinned items must appear in the URL-defined order, not the section order
  pinned.sort((a, b) => (order.get(getId(a)) ?? 0) - (order.get(getId(b)) ?? 0));
  return [...pinned, ...rest];
};
